import { Markdown } from '@davidlloydleisure/common-js-frontend';
import classNames from 'classnames';
import capitalize from 'lodash/capitalize';
import type { PropsWithChildren } from 'react';
import React, { use } from 'react';

import { WebsiteLink } from 'src/components/common/website-link';
import RedesignRightChevronThin from 'src/components/icons/redesign-right-chevron-thin';
import type { HeaderButtonProps } from 'src/components/layout/redesign/header-mobile/header-button';
import type { BaseHeaderContent } from 'src/components/layout/redesign/header-mobile/header-menu';
import HeaderMobileSecondaryNav from 'src/components/layout/redesign/header-mobile/header-secondary-nav';
import { SiteContext } from 'src/context/site-context';
import { amplitudeBrandName, amplitudeTrackEvent } from 'src/helpers/amplitude-event-track';
import { getBrandForBuildType } from 'src/helpers/brand-for-build-type';
import { onMarkdownLinkClick } from 'src/helpers/markdown-helper';
import { pathToTitle } from 'src/helpers/string-helper';

import HeaderMobileSubMenuWrapper from './header-sub-menu-wrapper';
import * as styles from './header-sub-menu.module.scss';

type SubMenuLink =
  | {
      name: string;
      link: string;
      onClick?: null;
    }
  | {
      name: string;
      link?: null;
      onClick: () => void;
    };

type SubMenuLinkProps = PropsWithChildren<{
  link: SubMenuLink;
  className: string;
  enabled: boolean;
  onClickLink?: () => void;
}>;

const SubMenuLinkComponent = ({
  link,
  className,
  children,
  enabled,
  onClickLink,
}: SubMenuLinkProps) => {
  const { pageLevel, buildType, language } = use(SiteContext);
  const handleClickButton = () => {
    void amplitudeTrackEvent(`Click to Submenu - Navigation`, {
      custom_page: window.location.origin,
      custom_level: pageLevel,
      brand: amplitudeBrandName(buildType),
      buildType,
      language,
      linkName: capitalize(link.name || ''),
      linkPath: pathToTitle(link.link || ''),
      component: 'Header Sub-Menu',
    });
  };

  const handleClickLink = () => {
    onClickLink?.();
    handleClickButton();
  };

  if (link.link !== null && link.link !== undefined) {
    return (
      <WebsiteLink to={link.link} handleClick={handleClickLink}>
        <div className={className}>{children}</div>
      </WebsiteLink>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        handleClickButton();
        link.onClick();
      }}
      className={className}
      disabled={!enabled}
    >
      {children}
    </button>
  );
};

export type HeaderMobileSubMenuProps = {
  isMenuOpen: boolean;
  isVisible: boolean;
  isChildMenuVisible: boolean;
  level: number;
  title?: string;
  description?: string | null;
  links?: SubMenuLink[];
  headerContent?: BaseHeaderContent;
  onFinish?: () => void;
  primaryButton?: HeaderButtonProps;
  secondaryButton?: HeaderButtonProps;
  searchButton?: HeaderButtonProps;
  linkDropdownCta?: Queries.LinkDropdownCta;
};

const HeaderMobileSubMenu = (props: HeaderMobileSubMenuProps) => {
  const { styling, buildType, isMeridian, isHarbour, language } = use(SiteContext);

  return (
    <HeaderMobileSubMenuWrapper
      isMenuOpen={props.isMenuOpen}
      isVisible={props.isVisible}
      isChildMenuVisible={props.isChildMenuVisible}
      level={props.level}
      primaryButton={props.primaryButton}
      secondaryButton={props.secondaryButton}
      searchButton={props.searchButton}
      linkDropdownCta={props.linkDropdownCta}
    >
      {(props.title || props.description) && (
        <div className={styles.borderBottom}>
          {props.title && <div className={styles.title}>{props.title}</div>}
          {props.description && (
            <Markdown
              markdownText={props.description}
              colorOverride={isMeridian ? styling.colors.meridianBlue : styling.colors.capeCod}
              brand={getBrandForBuildType(buildType)}
              onLinkClick={onMarkdownLinkClick(buildType, language, 'Header SubMenu')}
              classNameToOverride={styles.headerSubMenuText}
            />
          )}
        </div>
      )}
      {(props.links ?? []).map((link) => (
        <SubMenuLinkComponent
          key={link.name}
          link={link}
          className={classNames(
            styles.link,
            props.level === 1 ? styles.linkLevelOne : styles.linkLevelDeep,
            styles.borderBottom,
            props.level > 1 && !isMeridian && styles.largerPaddingLeft
          )}
          enabled={props.isVisible}
          onClickLink={props.onFinish}
        >
          <div>{link.name}</div>
          <RedesignRightChevronThin
            size="0.7rem"
            fill={isHarbour ? styling.colors.harbourGold : styling.colors.capeCod}
          />
        </SubMenuLinkComponent>
      ))}
      {props.level === 1 && (
        <HeaderMobileSecondaryNav headerContent={props.headerContent} onClick={props.onFinish} />
      )}
    </HeaderMobileSubMenuWrapper>
  );
};

export default HeaderMobileSubMenu;
